/**
 * Manifesto — the line-by-line reveal.
 *
 * The paragraph never moves. A bar in the section's ground colour is laid over
 * each rendered line, and the bars FADE OUT in sequence, so the lines arrive one
 * after another exactly where they already sit. See the note at the head of
 * sections/manifesto.liquid for why the reveal takes this shape rather than the
 * rise-and-fade every other section below the hero uses.
 *
 * The bars retracted left to right until 2026-08-10. That is the one thing this
 * reveal must not do: uncovering a line from one end is a moving boundary with a
 * cursor edge, which reads as text being typed however slowly it runs. Fading
 * has no direction, so the sequence survives and the typewriter does not. This
 * file is unchanged by that except for the property it waits on to tear down —
 * the animation itself lives entirely in the section's stylesheet.
 *
 * WHAT THIS FILE DOES NOT DO
 *
 * It never touches the paragraph's own markup. The usual way to animate text by
 * line is to split it into per-line spans, which means cutting across whatever
 * inline elements the copy contains — this section's stylesheet explicitly
 * supports <strong> and links inside the richtext, and both would have to be
 * reassembled around every line break. An overlay avoids the problem outright:
 * the DOM the merchant authored is left alone, and the accessibility tree still
 * contains exactly one paragraph.
 *
 * It also never hides anything it cannot later show. Bars are the only thing
 * that conceals the type, and bars only exist once they have been successfully
 * built. Every early return below therefore leaves the paragraph fully visible.
 *
 * TIMING
 *
 * Retraction is released by `.reveal--in`, which assets/reveal.js adds when the
 * section scrolls into view. This file only builds the bars and sets each one's
 * delay; it observes nothing and owns no trigger of its own.
 */
(() => {
  'use strict';

  if (window.__liturgyManifesto) return;
  window.__liturgyManifesto = true;

  const SELECTOR = '.manifesto[data-scroll-reveal]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Measurement cannot start until Reklam is actually in use.
   *
   * The MEASURE note in sections/manifesto.liquid records that this paragraph's
   * rag is only correct for a narrow window of measures, and that Reklam's GPOS
   * kerning makes its line breaks impossible to predict from metrics. A
   * fallback face therefore does not merely shift the lines slightly — it
   * produces a different number of them. Bars measured against it would be
   * laid over lines that are about to stop existing.
   */
  const FONT_TIMEOUT = 1500;

  /* Rects belonging to one line share a top, but not to the last decimal once
     an inline element with different metrics is involved. */
  const LINE_TOLERANCE = 2;

  /* Grown a hair past the ink so an antialiased edge never shows a sliver of
     glyph through the bar. */
  const BLEED = 1;

  /* Only reached when data-reveal-gap is missing, which the section never omits
     while the reveal is on. Kept in step with the "Gap between lines" default in
     sections/manifesto.liquid all the same: a fallback that silently disagrees
     with the shipped value is a bug waiting for the first caller that needs it. */
  const DEFAULT_GAP = 180;

  /**
   * Resolve when the real face is ready, or when waiting has stopped being
   * worth it. The race matters: document.fonts.ready can hang indefinitely
   * behind a font request that never settles, and a paragraph with no bars is a
   * far better outcome than one that is never measured.
   */
  const fontsReady = () => {
    if (!document.fonts || !document.fonts.ready) return Promise.resolve();
    return Promise.race([
      document.fonts.ready,
      new Promise((resolve) => window.setTimeout(resolve, FONT_TIMEOUT))
    ]);
  };

  /**
   * The rendered line boxes of an element, relative to `origin`.
   *
   * Measured one TEXT NODE at a time, deliberately, rather than by selecting
   * the container's contents in a single range.
   *
   * getClientRects() on a range returns the border box of every element the
   * range FULLY CONTAINS, on top of the line boxes. Selecting the contents of
   * .manifesto__text therefore also returns one rect for the whole <p> inside
   * it — a rect the height of the entire paragraph, which survives grouping as
   * a spurious extra "line" and produces one bar covering everything. It is not
   * filterable by size either: a one-line paragraph's block box and its single
   * line box are the same height.
   *
   * A range over a text node contains no elements, so it can only ever yield
   * line boxes. Walking the nodes also keeps inline markup working: a line
   * holding a <strong> is two text nodes sharing a top, and the grouping below
   * merges them back into one line. Grouping is keyed on the top first seen for
   * a line, so the key cannot drift as the group grows.
   */
  const lineBoxes = (el, origin) => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    const rects = [];

    let node;
    while ((node = walker.nextNode())) {
      /* Whitespace between block tags renders no box, and asking for its rects
         yields a zero-width one that would widen the line's left edge. */
      if (!node.nodeValue || !node.nodeValue.trim()) continue;

      range.selectNodeContents(node);
      for (const r of range.getClientRects()) {
        if (r.width > 0 && r.height > 0) rects.push(r);
      }
    }

    const lines = [];
    for (const r of rects) {
      const line = lines.find((l) => Math.abs(l.key - r.top) <= LINE_TOLERANCE);
      if (line) {
        line.left = Math.min(line.left, r.left);
        line.right = Math.max(line.right, r.right);
        line.top = Math.min(line.top, r.top);
        line.bottom = Math.max(line.bottom, r.bottom);
      } else {
        lines.push({ key: r.top, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
      }
    }

    /* getClientRects() is in document order, which is reading order for normal
       text — but not for anything the copy might later put out of flow. Sorting
       makes the stagger follow the page rather than the markup. */
    lines.sort((a, b) => a.top - b.top);

    return lines.map((l) => ({
      left: l.left - origin.left - BLEED,
      top: l.top - origin.top - BLEED,
      width: (l.right - l.left) + BLEED * 2,
      height: (l.bottom - l.top) + BLEED * 2
    }));
  };

  const build = (section) => {
    const inner = section.querySelector('.manifesto__inner');
    const text = section.querySelector('.manifesto__text');
    if (!inner || !text) return;

    /* Already uncovered. Bars created now would inherit the revealed state at
       birth, animate nothing, and sit there as dead nodes. */
    if (section.classList.contains('reveal--in')) return;

    const previous = inner.querySelector('.manifesto__masks');
    if (previous) previous.remove();

    const lines = lineBoxes(text, inner.getBoundingClientRect());
    if (!lines.length) return;

    const gap = Number(section.dataset.revealGap) || DEFAULT_GAP;

    const layer = document.createElement('div');
    layer.className = 'manifesto__masks';
    /* The bars carry no information — they are the absence of the paragraph,
       which is itself still in the tree and still read normally. */
    layer.setAttribute('aria-hidden', 'true');

    lines.forEach((line, i) => {
      const bar = document.createElement('div');
      bar.className = 'manifesto__mask';
      bar.style.left = line.left + 'px';
      bar.style.top = line.top + 'px';
      bar.style.width = line.width + 'px';
      bar.style.height = line.height + 'px';
      /* Set here rather than in the stylesheet because the line count is not
         knowable until this measurement has happened: it falls out of the copy,
         the viewport and the loaded face together. */
      bar.style.setProperty('--mask-d', (i * gap) + 'ms');
      layer.appendChild(bar);
    });

    /* Torn down once the last bar has finished, so the section ends the sequence
       with exactly the DOM it started with. If the transition never runs the
       layer simply stays, fully transparent and inert.

       'opacity', NOT 'transform'. The bars faded rather than retracted from
       2026-08-10; while this still read 'transform' the event never matched, the
       layer was never removed, and six transparent absolutely-positioned divs
       stayed in the DOM for the rest of the session. Harmless to look at and
       easy to miss — keep this in step with whatever property the mask
       transitions in sections/manifesto.liquid. */
    const last = layer.lastElementChild;
    last.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'opacity') layer.remove();
    }, { once: true });

    inner.appendChild(layer);
  };

  /**
   * Re-measure while the section is still covered.
   *
   * A resize re-wraps the paragraph, and bars measured at the old width would
   * be left sitting over the wrong lines. Observing the text rather than its
   * container keeps this away from its own output — the bar layer is absolutely
   * positioned and changes no layout, but observing the box it is appended to
   * invites a feedback loop for no benefit.
   */
  const watch = (section) => {
    if (!('ResizeObserver' in window)) return;

    const text = section.querySelector('.manifesto__text');
    if (!text) return;

    let frame = null;
    const observer = new ResizeObserver(() => {
      if (section.classList.contains('reveal--in')) {
        observer.disconnect();
        return;
      }
      /* Coalesced to one rebuild per frame; a drag-resize fires this
         continuously. */
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => build(section));
    });

    observer.observe(text);
  };

  const init = async (root) => {
    /* A visitor who asked for less motion gets no bars at all, which is the
       same outcome assets/reveal.js produces by withholding .reveal: the
       paragraph is simply there. */
    if (reducedMotion.matches) return;

    const scope = root || document;
    const sections = [];
    if (scope.matches && scope.matches(SELECTOR)) sections.push(scope);
    if (scope.querySelectorAll) sections.push(...scope.querySelectorAll(SELECTOR));

    const fresh = sections.filter((s) => !s.__manifestoBound);
    if (!fresh.length) return;
    for (const s of fresh) s.__manifestoBound = true;

    await fontsReady();

    for (const section of fresh) {
      build(section);
      watch(section);
    }
  };

  init(document);

  /* `shopify theme dev`'s hot reload and the theme editor both replace this
     section's markup wholesale, and the replacement arrives without bars and
     without the binding flag above. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
